<script lang="ts">
  import { onMount } from "svelte"
  import type {
      InboxWorkflowCommitResult,
      InboxWorkflowCustomStepProps,
      InboxWorkflowRowControls,
  } from "@kontextwerk/control-web-frame/shared/logic/inbox-workflow"
  import { userDisplayName } from "@kontextwerk/control-web-frame/shared/logic/user-presentation"
  import ContactOrderPlanningSurface from "../../../../../../../../../../_shared/_modules/contact-order-planning-surface"
  import type {
    ContactOrderPlanningCreateCommandInput,
    ContactOrderPlanningCreateOperationResult,
    ContactOrderPlanningOperations,
    ContactOrderPlanningRowPresentation,
    ContactOrderPlanningSubmissionResult,
  } from "../../../../../../../../../../_shared/_logic/contact-orders-contract"
  import { buildContactOrderBatchPayloads } from "../../../../../../../../../../_shared/_logic/contact-orders-payload"
  import { projectContactOrderCreateResult } from "../../../../../../../../../../_shared/_logic/create-result"
  import { filterSelectableOrganizationUsers } from "../../../../../../../../../../_shared/_logic/organization-users"
  import type {
    ContactOrder,
    ContactOrderBatchPreviewResponse,
    ContactOrderBatchResponse,
  } from "../../../../../../../../../../../../api/kehrblatt/contact-orders"
  import type { InboxCaseBatchWorkflow } from "../../../../../../../../../../../../api/kehrblatt/inbox-cases"
  import { buildInboxCustomStepRowPresentations } from "../../../../_shared/_logic/custom-step-rows"
  import { buildInformCustomerSourceData, stringValue } from "./_logic/sourceRows"
  import { inboxStepPayload, projectCustomerInformationSuccess } from "./_logic/payload"
  import type { InboxContactOrderPayload } from "./_logic/payload"
  import { payloadActionCaseId, sourceForPayload } from "./_logic/operations"
  import type { InformCustomerSourcePreview } from "./contract"
  import { parseInformCustomerPreview } from "./_logic/preview"

  type PlanningUser = NonNullable<InboxWorkflowCustomStepProps["organization"]>["users"][number]

  let props: InboxWorkflowCustomStepProps = $props()

  const STEP_KEY = "inform_customer"

  const workflow = $derived(props.workflow as unknown as InboxCaseBatchWorkflow)
  const rowControls = $derived(
    (props.extra?.rowControls ?? null) as InboxWorkflowRowControls | null,
  )
  const rowPresentations = $derived(
    buildInboxCustomStepRowPresentations({
      workflow: props.workflow,
      step: props.step,
      rowOutcomes: props.rowOutcomes,
      rowControls,
    }),
  )
  const organizationId = $derived(
    stringValue(
      props.organizationId,
      props.organization?.id,
      workflow.items[0]?.action_case.organization_id,
    ),
  )
  const activeUsers = $derived(
    // Appointment employees are memberships; selectable users carry the membership as `id`.
    filterSelectableOrganizationUsers((props.organization?.users ?? []) as PlanningUser[], null),
  )
  const employeeOptions = $derived(
    activeUsers.map((user) => ({
      label: userDisplayName(user) || "Unbekannt",
      value: String(user.id),
    })),
  )
  const hiddenActionCaseIds = $derived(
    Object.values(rowPresentations)
      .filter((presentation) => presentation.removedEarlier)
      .map((presentation) => presentation.actionCaseId),
  )
  const hiddenActionCaseIdSet = $derived(new Set(hiddenActionCaseIds))
  let sourcePreview = $state<InformCustomerSourcePreview | null>(null)
  let sourceLoading = $state(true)
  let sourceError = $state<string | null>(null)
  const persistedOutputRevision = $derived(
    workflow.items
      .map((item) => {
        const persistedStep = item.steps.find((candidate) => candidate.step_key === STEP_KEY)

        return `${item.action_case.id}:${persistedStep?.status ?? ""}:${JSON.stringify(persistedStep?.output ?? {})}`
      })
      .join("|"),
  )
  const sourceSurfaceKey = $derived(
    sourceLoading ? "loading" : sourcePreview ? `ready:${persistedOutputRevision}` : "error",
  )
  const sourceData = $derived(
    organizationId && sourcePreview
      ? buildInformCustomerSourceData({ workflow, preview: sourcePreview, organizationId })
      : null,
  )
  const sourceRows = $derived(
    (sourceData?.rows ?? []).filter((row) => !hiddenActionCaseIdSet.has(row.actionCaseId)),
  )
  const sourceByActionCaseId = $derived(
    Object.fromEntries(sourceRows.map((row) => [row.actionCaseId, row])),
  )
  const actionCaseIdByScheduleEventId = $derived(
    Object.fromEntries(sourceRows.map((row) => [row.event.id, row.actionCaseId])),
  )
  const initialRemovedEventIds = $derived(
    sourceRows
      .filter((row) => rowPresentations[row.actionCaseId]?.removedCurrent)
      .map((row) => row.event.id),
  )
  const rowPresentationByEventId = $derived<Record<string, ContactOrderPlanningRowPresentation>>(
    Object.fromEntries(
      sourceRows.map((row) => {
        const presentation = rowPresentations[row.actionCaseId]

        if (!presentation) return [row.event.id, {}]

        const removedCurrent = presentation.removedCurrent

        return [
          row.event.id,
          {
            rowNumber: presentation.rowNumber,
            disabled: removedCurrent ? false : presentation.disabled,
            selectable: removedCurrent ? true : presentation.selectable,
            muted: removedCurrent ? false : presentation.muted,
            disabledReason: removedCurrent ? null : presentation.disabledReason,
            mutedReason: removedCurrent ? null : presentation.mutedReason,
          } satisfies ContactOrderPlanningRowPresentation,
        ]
      }),
    ),
  )
  const workOrders = $derived(sourceRows.map((row) => row.workOrder))
  const events = $derived(sourceRows.map((row) => row.event))
  const visiblePropertyIds = $derived(new Set(workOrders.map((workOrder) => workOrder.property_id)))
  const properties = $derived(
    (sourceData?.properties ?? []).filter((property) => visiblePropertyIds.has(property.id)),
  )
  const customers = $derived(sourceData?.customers ?? [])
  const contactPoints = $derived(sourceData?.contactPoints ?? [])
  const contactOrders = $derived(sourceData?.contactOrders ?? [])
  let surfaceErrorMessage = $state<string | null>(null)

  const buildInboxPayloads = (input: Parameters<typeof buildContactOrderBatchPayloads>[0]) =>
    buildContactOrderBatchPayloads(input).map((payload) => ({
      ...payload,
      action_case_id:
        (payload.schedule_event_id
          ? actionCaseIdByScheduleEventId[payload.schedule_event_id]
          : null) ?? null,
    }))

  const buildInboxActionItems = (payloads: InboxContactOrderPayload[]) => {
    if (!organizationId) throw new Error("Organisation fehlt.")

    return payloads.map((payload) => {
      const actionCaseId = payloadActionCaseId(payload)
      const source = sourceForPayload({
        payload,
        sourceByActionCaseId,
      })

      if (!actionCaseId || !source) {
        throw new Error("Kundeninformation konnte keiner Inbox-Zeile zugeordnet werden.")
      }

      return {
        action_case_id: actionCaseId,
        payload: inboxStepPayload(payload, source),
      }
    })
  }

  const previewViaInbox = async (
    payloads: InboxContactOrderPayload[],
  ): Promise<ContactOrderBatchPreviewResponse> => {
    if (!payloads.length) return { previews: [], failed: [], cost_benefit: null }

    const items = buildInboxActionItems(payloads)

    if (!props.loadPreview) throw new Error("Preview-Adapter fehlt.")

    const result = await props.loadPreview({
      action_case_ids: items.map((item) => item.action_case_id),
      payload: {
        mode: "operations",
        row_inputs_by_action_case_id: Object.fromEntries(
          items.map((item) => [item.action_case_id, item.payload]),
        ),
      },
    })

    if (
      result.flow_key !== workflow.flow.flow_key ||
      result.step_key !== STEP_KEY ||
      result.preview_kind !== "unclear_appointment_feedback.inform_customer.operations"
    ) {
      throw new Error("Kundeninformations-Vorschau passt nicht zum angeforderten Step.")
    }

    const parsed = parseInformCustomerPreview(result.payload)

    if (parsed.mode !== "operations")
      throw new Error("Kundeninformations-Vorschau hat den falschen Modus.")

    return parsed.response
  }

  const createViaInboxCommit = async ({
    payloads,
    targetEvents,
    events,
    contactOrders,
  }: ContactOrderPlanningCreateCommandInput): Promise<
    ContactOrderPlanningCreateOperationResult<InboxWorkflowCommitResult>
  > => {
    if (!organizationId) throw new Error("Organisation fehlt.")

    const items = buildInboxActionItems(payloads)
    const payloadByActionCaseId = new Map<string, InboxContactOrderPayload>()
    for (const payload of payloads) {
      const actionCaseId = payloadActionCaseId(payload)

      if (actionCaseId) payloadByActionCaseId.set(actionCaseId, payload)
    }
    const receipt = await props.commit(items)
    const targetEventById = new Map(targetEvents.map((event) => [event.id, event]))
    const successfulInformation = receipt.rows
      .filter((row) => row.status === "success")
      .map((row) => {
        const payload = payloadByActionCaseId.get(row.action_case_id)
        const source = sourceByActionCaseId[row.action_case_id]

        if (!payload || !source) return null

        const eventId = payload.schedule_event_id ?? source.event.id

        return projectCustomerInformationSuccess({
          row,
          payload,
          source,
          event: targetEventById.get(eventId) ?? source.event,
          organizationId,
        })
      })
      .filter(
        (information): information is { event: ScheduleEvent; contactOrder: ContactOrder | null } =>
          information !== null,
      )
    const created = successfulInformation
      .map((information) => information.contactOrder)
      .filter((order): order is ContactOrder => order !== null)
    const successfulEventById = new Map(
      successfulInformation.map((information) => [information.event.id, information.event]),
    )
    const projectedTargetEvents = targetEvents.map(
      (event) => successfulEventById.get(event.id) ?? event,
    )
    const projectedEvents = events.map((event) => successfulEventById.get(event.id) ?? event)
    const completedCount = receipt.rows.filter((row) => row.status === "success").length
    const result: ContactOrderBatchResponse = {
      created,
      failed: receipt.rows
        .filter((row) => row.status !== "success")
        .map((row, index) => {
          const payload = payloadByActionCaseId.get(row.action_case_id)

          return {
            index,
            work_order_id: payload?.work_order_id ?? null,
            schedule_event_id: payload?.schedule_event_id ?? null,
            detail: row.error || row.detail || "Kundeninformation konnte nicht vorbereitet werden.",
          }
        }),
    }
    const projection = projectContactOrderCreateResult({
      result,
      targetEvents: projectedTargetEvents,
      events: projectedEvents,
      contactOrders,
    })

    return { result, ...projection, completedCount, receipt }
  }

  const operations = $derived<ContactOrderPlanningOperations<InboxWorkflowCommitResult>>({
    previewContactOrders: previewViaInbox,
    createContactOrders: createViaInboxCommit,
  })

  const actionCaseIdsForEventIds = (eventIds: string[]) =>
    Array.from(
      new Set(
        eventIds
          .map((eventId) => actionCaseIdByScheduleEventId[eventId])
          .filter((actionCaseId): actionCaseId is string => Boolean(actionCaseId)),
      ),
    )

  const handleRowsRemoved = (eventIds: string[]) => {
    const actionCaseIds = actionCaseIdsForEventIds(eventIds)

    if (actionCaseIds.length) rowControls?.removeActionCasesFromCurrentStep(actionCaseIds)
  }

  const handleRowsRestored = (eventIds: string[]) => {
    const actionCaseIds = actionCaseIdsForEventIds(eventIds)

    if (actionCaseIds.length) rowControls?.restoreActionCasesForCurrentStep(actionCaseIds)
  }

  onMount(() => {
    if (!organizationId) {
      sourceLoading = false

      return
    }

    let active = true
    const actionCaseIds = workflow.items
      .filter(
        (item) =>
          item.compatible &&
          (item.steps.some((step) => step.step_key === STEP_KEY) ||
            item.action_case.requirements.includes("INFORM_CUSTOMER")),
      )
      .map((item) => item.action_case.id)

    if (!props.loadPreview) {
      sourceError = "Kundeninformationsdaten konnten nicht geladen werden."
      sourceLoading = false

      return
    }

    void props
      .loadPreview({
        action_case_ids: actionCaseIds,
        payload: { mode: "source" },
      })
      .then((result) => {
        if (!active) return

        if (
          result.flow_key !== workflow.flow.flow_key ||
          result.step_key !== STEP_KEY ||
          result.preview_kind !== "unclear_appointment_feedback.inform_customer.source"
        ) {
          throw new Error("Kundeninformations-Source-Preview passt nicht zum angeforderten Step.")
        }

        const parsed = parseInformCustomerPreview(result.payload)

        if (parsed.mode !== "source") {
          throw new Error("Kundeninformations-Source-Preview hat den falschen Modus.")
        }

        sourcePreview = parsed
        sourceError = null
      })
      .catch(() => {
        if (!active) return

        sourcePreview = null
        sourceError = "Kundeninformationsdaten konnten nicht geladen werden."
      })
      .finally(() => {
        if (!active) return

        sourceLoading = false
      })

    return () => {
      active = false
    }
  })

  const handleSubmissionResult = async (
    result: ContactOrderPlanningSubmissionResult<InboxWorkflowCommitResult>,
  ) => {
    if (result.kind === "committed") await props.complete(result.receipt)
  }
</script>

{#key sourceSurfaceKey}
  <ContactOrderPlanningSurface
    {organizationId}
    {events}
    {workOrders}
    {properties}
    {customers}
    {contactPoints}
    {employeeOptions}
    {contactOrders}
    initialFilterStatus="all"
    {rowPresentationByEventId}
    {initialRemovedEventIds}
    allowRemoveOnlySubmission={true}
    {operations}
    buildPayloads={buildInboxPayloads}
    createButtonLabel="Kunden informieren"
    successMessage={(createdCount) => `Kundeninformationen vorbereitet: ${createdCount}.`}
    helpModalTextId={props.help?.textId ?? ""}
    showContinueButton={false}
    readOnly={props.readOnly}
    loading={!organizationId || sourceLoading}
    setError={(message) => (surfaceErrorMessage = message)}
    onRowsRemoved={handleRowsRemoved}
    onRowsRestored={handleRowsRestored}
    onSubmissionResult={handleSubmissionResult}
  />
{/key}

{#if sourceError || sourceData?.errors.length || surfaceErrorMessage}
  <div class="surface-error">
    {#if sourceError}<p>{sourceError}</p>{/if}
    {#each sourceData?.errors ?? [] as error}
      <p>{error.message}</p>
    {/each}
    {#if surfaceErrorMessage}<p>{surfaceErrorMessage}</p>{/if}
  </div>
{/if}

<style>
  .surface-error {
    margin: 0.75rem 0 0;
    color: #b91c1c;
    font-weight: 700;
  }
</style>
